import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { BankingFilters, BankTransactionSort } from '@/types/banking'
import { NO_CATEGORY_FILTER_VALUE, isBankTransactionSortKey } from '@/types/banking'
import { getDefaultDateRange } from '@/lib/date-utils'
import { UUID_REGEX, PLAID_CATEGORY_REGEX } from '@dcp/shared/utils/validators'

/**
 * Hook that syncs BankingFilters state with URL search params.
 * Replaces useState for filters in the Banking Dashboard page.
 *
 * - Reads filter values from URL params, falling back to defaults for dates
 * - Updates URL params via setFilters (with replace: true to avoid history pollution)
 * - Resets page to 1 when filters change
 * - Provides page state and setter
 * - Validates category values against allowed types
 * - DCP-123: syncs the table sort (`sort` + `dir` params) — see `sort`/`setSort`
 *
 * @returns Object containing filters, setFilters, clearFilters, page, setPage,
 *          sort and setSort
 */
export function useBankingFilterParams() {
  const [searchParams, setSearchParams] = useSearchParams()

  /**
   * Derived filters object from current URL search params.
   * Default date range is computed fresh inside useMemo to avoid stale dates
   * when the app stays open across midnight.
   * Array filters (facilityIds, accountIds, categories) are stored as
   * comma-separated values in the URL and parsed back into arrays here.
   */
  const filters: BankingFilters = useMemo(() => {
    const defaultRange = getDefaultDateRange()
    const startDate = searchParams.get('startDate') ?? defaultRange.startDate
    const endDate = searchParams.get('endDate') ?? defaultRange.endDate

    // Parse comma-separated facilityIds, treat empty result as undefined
    const facilityIdsRaw = searchParams.get('facilityIds')?.split(',').filter(id => id && UUID_REGEX.test(id)) ?? []
    const facilityIds = facilityIdsRaw.length ? facilityIdsRaw : undefined

    // Parse comma-separated accountIds, treat empty result as undefined
    const accountIdsRaw = searchParams.get('accountIds')?.split(',').filter(id => id && UUID_REGEX.test(id)) ?? []
    const accountIds = accountIdsRaw.length ? accountIdsRaw : undefined

    // Parse comma-separated categories. Values are source-agnostic (DCP-223):
    // either a categories.category_id UUID (manual / file-import / categorized
    // Plaid rows) or a legacy Plaid category string (e.g. FOOD_AND_DRINK).
    // DCP-123 (AC#5): plus the literal NO_CATEGORY_FILTER_VALUE — "uncategorized".
    const categoriesRaw = searchParams.get('categories')?.split(',').filter(c =>
      c && (c === NO_CATEGORY_FILTER_VALUE || UUID_REGEX.test(c) || PLAID_CATEGORY_REGEX.test(c))
    ) ?? []
    const categories = categoriesRaw.length ? categoriesRaw : undefined

    return {
      startDate,
      endDate,
      facilityIds,
      accountIds,
      categories,
    }
  }, [searchParams])

  /**
   * Current page number from URL params (defaults to 1).
   */
  const page = useMemo(() => {
    const pageParam = searchParams.get('page')
    const pageNumber = pageParam ? parseInt(pageParam, 10) : 1
    return isNaN(pageNumber) || pageNumber < 1 ? 1 : pageNumber
  }, [searchParams])

  /**
   * DCP-123 (AC#6): the table sort from the URL (`?sort=<key>&dir=asc|desc`),
   * or undefined = the default order (newest first). Kept in the URL like the
   * filters so a shared / reloaded link shows the same order. An unknown key
   * means no sort; a missing or unknown `dir` means ascending — the direction a
   * first click on a column header produces.
   */
  // Memoized on the two raw strings, not on `searchParams`: every page / filter
  // change produces a new URLSearchParams, and a fresh `sort` object each time
  // would hand consumers a new reference for an unchanged sort.
  const sortParam = searchParams.get('sort')
  const dirParam = searchParams.get('dir')
  const sort: BankTransactionSort | undefined = useMemo(() => {
    if (!isBankTransactionSortKey(sortParam)) return undefined
    return { key: sortParam, direction: dirParam === 'desc' ? 'desc' : 'asc' }
  }, [sortParam, dirParam])

  /**
   * Updates filters in URL search params.
   * Removes params when value is undefined/empty.
   * Resets page to 1 when filters change.
   * Uses replace: true to avoid polluting browser history.
   *
   * @param newFilters - Partial or complete BankingFilters object
   */
  const setFilters = useCallback(
    (newFilters: BankingFilters) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)

          // Update or delete each filter param
          if (newFilters.startDate) {
            next.set('startDate', newFilters.startDate)
          } else {
            next.delete('startDate')
          }

          if (newFilters.endDate) {
            next.set('endDate', newFilters.endDate)
          } else {
            next.delete('endDate')
          }

          // Write array filters as comma-separated strings; delete when empty
          if (newFilters.facilityIds?.length) {
            next.set('facilityIds', newFilters.facilityIds.join(','))
          } else {
            next.delete('facilityIds')
          }

          if (newFilters.accountIds?.length) {
            next.set('accountIds', newFilters.accountIds.join(','))
          } else {
            next.delete('accountIds')
          }

          if (newFilters.categories?.length) {
            next.set('categories', newFilters.categories.join(','))
          } else {
            next.delete('categories')
          }

          // Reset page to 1 when filters change
          next.set('page', '1')

          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  /**
   * Resets filters to defaults (last 30 days date range).
   * Computes fresh default range to avoid stale dates.
   * Removes all filter params except startDate, endDate and the sort (sort/dir).
   * Resets page to 1.
   *
   * DCP-123: the table SORT survives — "clear filters" is about which rows are
   * shown, not the order the user picked for them (the column header still
   * shows its arrow, so dropping the order would contradict it). Unknown
   * params are still dropped, as before.
   */
  const clearFilters = useCallback(() => {
    const defaultRange = getDefaultDateRange()
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams({
          startDate: defaultRange.startDate,
          endDate: defaultRange.endDate,
          page: '1',
        })
        const sortKey = prev.get('sort')
        if (isBankTransactionSortKey(sortKey)) {
          next.set('sort', sortKey)
          const dir = prev.get('dir')
          if (dir === 'asc' || dir === 'desc') next.set('dir', dir)
        }
        return next
      },
      { replace: true }
    )
  }, [setSearchParams])

  /**
   * DCP-123 (AC#6): updates the table sort in URL params. `undefined` removes
   * both params (back to the default newest-first order). Resets page to 1 —
   * page 3 of a different order is an arbitrary slice — and uses
   * replace: true like setFilters so sorting doesn't pollute history.
   *
   * @param newSort - the sort to apply, or undefined for the default order
   */
  const setSort = useCallback(
    (newSort: BankTransactionSort | undefined) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (newSort) {
            next.set('sort', newSort.key)
            next.set('dir', newSort.direction)
          } else {
            next.delete('sort')
            next.delete('dir')
          }
          next.set('page', '1')
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  /**
   * Updates the page number in URL params.
   *
   * @param newPage - New page number (must be >= 1)
   */
  const setPage = useCallback(
    (newPage: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('page', String(Math.max(1, newPage)))
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  return {
    filters,
    setFilters,
    clearFilters,
    page,
    setPage,
    sort,
    setSort,
  }
}
